/**
 * ProfileCompletenessMeter
 *
 * Exports:
 *   - computeProfileCompletenessPct — pure % helper
 *   - buildCheckItems / CheckItem
 *   - PROFILE_COMPLETION_NAV_EVENT — dispatched after navigating to Account Settings
 *   - ProfileCompletionWidget (default) — floating FAB + expandable checklist card
 */
import {
  ArrowRight,
  Check,
  Minus,
} from 'lucide-react';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import type { UserProfile } from '../../../types/userProfile';

/** Dispatched on window after routing to Account Settings so the page can switch profile sub-tab. */
export const PROFILE_COMPLETION_NAV_EVENT = 'ggj:navigate-account-settings-profile' as const;

export interface ProfileCompletionNavDetail {
  subTab: 'work' | 'preferences' | 'resume';
  /** Resume checklist key — scrolls to matching accordion section when on Resume tab. */
  sectionKey?: string;
}

const PROFILE_WIDGET_MINIMIZED_SS_KEY = 'ggj.profile-completion-widget.minimized';
const PROFILE_WIDGET_HIDDEN_LS_KEY = 'ggj.profile-completion-widget.hidden';

function readMinimizedSnapshot(): boolean {
  try {
    const v = sessionStorage.getItem(PROFILE_WIDGET_MINIMIZED_SS_KEY);
    if (v === null) return true; // default collapsed on first visit
    return v === '1';
  } catch {
    return true;
  }
}

function writeMinimizedSnapshot(minimized: boolean): void {
  try {
    sessionStorage.setItem(PROFILE_WIDGET_MINIMIZED_SS_KEY, minimized ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function readHiddenPreference(): boolean {
  try {
    return localStorage.getItem(PROFILE_WIDGET_HIDDEN_LS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeHiddenPreference(hidden: boolean): void {
  try {
    if (hidden) {
      localStorage.setItem(PROFILE_WIDGET_HIDDEN_LS_KEY, '1');
    } else {
      localStorage.removeItem(PROFILE_WIDGET_HIDDEN_LS_KEY);
    }
  } catch {
    /* ignore */
  }
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

export interface CheckItem {
  key: string;
  label: string;
  weight: number;
  done: boolean;
  /** Account Settings profile sub-nav. */
  subTab: 'work' | 'preferences' | 'resume';
  /** Visual grouping label shown as a section header in the modal. */
  group: 'basics' | 'preferences' | 'resume';
}

function actionLabelFor(
  item: CheckItem,
  t: (key: string, defaultValue: string) => string,
): string {
  switch (item.key) {
    case 'displayName': return t('dashboard.profileCompletion.actions.displayName', 'Add your display name');
    case 'headline': return t('dashboard.profileCompletion.actions.headline', 'Add a headline');
    case 'currentRole': return t('dashboard.profileCompletion.actions.currentRole', 'Add your current role');
    case 'experience': return t('dashboard.profileCompletion.actions.experience', 'Add years of experience');
    case 'skills': return t('dashboard.profileCompletion.actions.skills', 'Add at least 3 skills');
    case 'industry': return t('dashboard.profileCompletion.actions.industry', 'Set your industry');
    case 'location': return t('dashboard.profileCompletion.actions.location', 'Add your location');
    case 'salary': return t('dashboard.profileCompletion.actions.salary', 'Set salary expectations');
    case 'links': return t('dashboard.profileCompletion.actions.links', 'Link your GitHub/Portfolio');
    case 'summary': return t('dashboard.profileCompletion.actions.summary', 'Add a professional summary');
    case 'workExp': return t('dashboard.profileCompletion.actions.workExp', 'Add previous job description');
    case 'education': return t('dashboard.profileCompletion.actions.education', 'Add your education');
    case 'certOrLang': return t('dashboard.profileCompletion.actions.certOrLang', 'Add certifications or languages');
    default: return item.label;
  }
}

export function buildCheckItems(profile: UserProfile | null): CheckItem[] {
  const hasLink =
    !!(profile?.linkedinUrl?.trim()) ||
    !!(profile?.githubUrl?.trim()) ||
    !!(profile?.portfolioUrl?.trim());

  return [
    { key: 'displayName', label: 'Display name',        weight: 8,  done: !!(profile?.displayName?.trim()),             subTab: 'work',        group: 'basics' },
    { key: 'headline',    label: 'Headline',             weight: 7,  done: !!(profile?.headline?.trim()),                subTab: 'work',        group: 'basics' },
    { key: 'currentRole', label: 'Current role / title', weight: 7,  done: !!(profile?.currentRole?.trim()),             subTab: 'work',        group: 'basics' },
    { key: 'experience',  label: 'Years of experience',  weight: 6,  done: profile?.experienceYears != null,             subTab: 'work',        group: 'basics' },
    { key: 'skills',      label: 'Skills (min. 3)',       weight: 7,  done: (profile?.skills?.length ?? 0) >= 3,          subTab: 'work',        group: 'basics' },
    { key: 'industry',    label: 'Industry',              weight: 6,  done: !!(profile?.industry?.trim()),                subTab: 'preferences', group: 'preferences' },
    { key: 'location',    label: 'Location',              weight: 6,  done: !!(profile?.location?.trim()),                subTab: 'preferences', group: 'preferences' },
    { key: 'salary',      label: 'Salary expectation',    weight: 6,  done: profile?.salaryExpectationInr != null,        subTab: 'preferences', group: 'preferences' },
    { key: 'links',       label: 'Social / portfolio link', weight: 6, done: hasLink,                                    subTab: 'work',        group: 'basics' },
    { key: 'summary',     label: 'Professional summary',  weight: 9,  done: !!(profile?.professionalSummary?.trim()),     subTab: 'resume',      group: 'resume' },
    { key: 'workExp',     label: 'Work experience',        weight: 11, done: (profile?.experiences?.length ?? 0) > 0,     subTab: 'resume',      group: 'resume' },
    { key: 'education',   label: 'Education',              weight: 8,  done: (profile?.education?.length ?? 0) > 0,       subTab: 'resume',      group: 'resume' },
    { key: 'certOrLang',  label: 'Certifications or languages', weight: 5, done:
        (profile?.certifications?.length ?? 0) > 0 || (profile?.languages?.length ?? 0) > 0, subTab: 'resume', group: 'resume' },
  ];
}

export function computeProfileCompletenessPct(profile: UserProfile | null): number {
  const items = buildCheckItems(profile);
  const total = items.reduce((s, i) => s + i.weight, 0);
  const done = items.reduce((s, i) => s + (i.done ? i.weight : 0), 0);
  return Math.round((done / total) * 100);
}

function guideTextFor(pct: number, t: (key: string, defaultValue: string) => string): string {
  if (pct >= 100) return t('dashboard.profileCompletion.guideComplete', 'Your profile is complete — keep it fresh!');
  if (pct >= 60) return t('dashboard.profileCompletion.guideAlmost', 'Almost there! Complete these to hit 100%:');
  if (pct >= 40) return t('dashboard.profileCompletion.guideProgress', 'Good progress! Complete these to unlock more matches:');
  return t('dashboard.profileCompletion.guideStart', 'Get started — complete these to boost your profile:');
}

const BRAND_PURPLE = 'var(--ds-brand)';
const TRACK_PURPLE = 'var(--ds-brand-soft)';
const BODY_SLATE = 'var(--ds-text-body)';

// ── Animated percentage ───────────────────────────────────────────────────────

function useAnimatedPct(target: number, durationMs = 450): number {
  const [display, setDisplay] = useState(target);
  const displayRef = useRef(target);

  useEffect(() => {
    if (target === displayRef.current) return;
    const from = displayRef.current;
    const at = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const t = Math.min(1, (now - at) / durationMs);
      const eased = 1 - (1 - t) ** 3;
      const next = Math.round(from + (target - from) * eased);
      displayRef.current = next;
      setDisplay(next);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        displayRef.current = target;
        setDisplay(target);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return display;
}

// ── Progress ring ─────────────────────────────────────────────────────────────

interface ProgressRingProps {
  pct: number;
  size: 'sm' | 'lg';
  showPct?: boolean;
}

const ProgressRing: React.FC<ProgressRingProps> = ({ pct, size, showPct = true }) => {
  const isLarge = size === 'lg';
  const vb = isLarge ? 120 : 52;
  const r = isLarge ? 46 : 20;
  const stroke = isLarge ? 8 : 4;
  const cx = vb / 2;
  const clampedPct = Math.min(100, Math.max(0, pct));

  return (
    <div
      className={`profile-completion-ring-wrap relative shrink-0 ${isLarge ? 'profile-completion-ring-wrap--lg h-[120px] w-[120px]' : 'h-[52px] w-[52px]'}`}
      aria-hidden
    >
      <svg
        viewBox={`0 0 ${vb} ${vb}`}
        className="profile-completion-ring-svg h-full w-full"
        aria-hidden
      >
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={TRACK_PURPLE}
          strokeWidth={stroke}
        />
        {clampedPct > 0 && (
          <circle
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke={BRAND_PURPLE}
            strokeWidth={stroke}
            strokeLinecap="butt"
            pathLength={100}
            strokeDasharray={clampedPct >= 100 ? undefined : `${clampedPct} ${100 - clampedPct}`}
            strokeDashoffset={0}
            transform={`rotate(-90 ${cx} ${cx})`}
            style={{ transition: 'stroke-dasharray 0.45s cubic-bezier(0.22, 1, 0.36, 1)' }}
          />
        )}
      </svg>
      {showPct && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span
            className={`t-num leading-none ${isLarge ? 't-hero' : 't-body t-strong'}`}
            style={{ color: 'var(--ds-text-strong, var(--vscode-text-primary))' }}
          >
            {clampedPct}%
          </span>
        </div>
      )}
    </div>
  );
};

// ── Checklist row ─────────────────────────────────────────────────────────────

const ChecklistRow: React.FC<{
  item: CheckItem;
  onNavigate: (subTab: CheckItem['subTab'], sectionKey?: string) => void;
}> = ({ item, onNavigate }) => {
  const { t } = useTranslation();
  const label = actionLabelFor(item, t);

  return (
    <button
      type="button"
      onClick={() => onNavigate(item.subTab, item.key)}
      className="profile-completion-checklist-row group flex w-full items-start gap-3 rounded-lg px-1 py-2 text-left transition-colors touch-manipulation"
      style={{ opacity: item.done ? 0.5 : 1 }}
    >
      <span
        className="profile-completion-checkbox mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[4px] border transition-colors"
        aria-hidden
      >
        {item.done && <Check className="h-3 w-3" strokeWidth={2.5} style={{ color: BRAND_PURPLE }} />}
      </span>
      <span className="t-body t-strong flex-1 leading-snug break-words">
        {label}
        {!item.done && (
          <span className="profile-completion-weight ml-1 font-medium">
            (+{item.weight}%)
          </span>
        )}
      </span>
    </button>
  );
};

// ── Expanded card ─────────────────────────────────────────────────────────────

const ExpandedCard: React.FC<{
  profile: UserProfile | null;
  pct: number;
  displayPct: number;
  onMinimize?: () => void;
  onNavigate: (subTab: CheckItem['subTab'], sectionKey?: string) => void;
  onCompleteProfile: () => void;
  showDismissOption?: boolean;
  onDismissForever?: () => void;
  className?: string;
}> = ({
  profile,
  pct,
  displayPct,
  onMinimize,
  onNavigate,
  onCompleteProfile,
  showDismissOption = false,
  onDismissForever,
  className = '',
}) => {
  const { t } = useTranslation();
  const pendingItems = useMemo(() => {
    return buildCheckItems(profile)
      .filter((i) => !i.done)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 3);
  }, [profile]);

  return (
    <div
      className={`profile-completion-card w-[min(300px,calc(100vw-2rem))] rounded-2xl border p-5 shadow-lg ${className}`}
      role="region"
      aria-label={t('dashboard.profileCompletion.ariaRegion', { defaultValue: 'Profile {{pct}}% complete', pct })}
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <span className="t-label">{t('dashboard.profileCompletion.heading', 'Profile completeness')}</span>
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            className="profile-completion-minimize ds-btn ds-btn-ghost ds-btn-sm touch-manipulation"
            aria-label={t('dashboard.profileCompletion.minimize', 'Minimize profile completeness')}
          >
            <Minus className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex flex-col items-center">
        <ProgressRing pct={displayPct} size="lg" />
        <p className="profile-completion-guide t-body mt-4 px-1 text-center leading-relaxed text-balance">
          {guideTextFor(pct, t)}
        </p>
      </div>

      {pendingItems.length > 0 && (
        <div className="mt-3 space-y-0.5">
          {pendingItems.map((item) => (
            <ChecklistRow key={item.key} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      )}

      {showDismissOption && onDismissForever && (
        <label className="profile-completion-dismiss mt-4 flex min-h-[44px] cursor-pointer items-center gap-2.5 touch-manipulation">
          <input
            type="checkbox"
            className="profile-completion-dismiss__input h-4 w-4 shrink-0 rounded border"
            onChange={(e) => {
              if (e.target.checked) onDismissForever();
            }}
          />
          <span className="t-body leading-snug">{t('dashboard.profileCompletion.dismissForever', "Don't show this again")}</span>
        </label>
      )}

      {pct < 100 && (
        <button
          type="button"
          onClick={onCompleteProfile}
          className="ds-btn ds-btn-primary ds-btn-lg mt-4 w-full"
        >
          {t('dashboard.profileCompletion.completeProfile', 'Complete Profile')}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
};

// ── Main widget ────────────────────────────────────────────────────────────────

export interface ProfileCompletionWidgetProps {
  profile: UserProfile | null;
  /** Navigate to Account Settings; widget dispatches PROFILE_COMPLETION_NAV_EVENT after tick. */
  onNavigate: (subTab: 'work' | 'preferences' | 'resume', sectionKey?: string) => void;
  /** `floating` = FAB + expandable card; `inline` = always-expanded card for profile page. */
  variant?: 'floating' | 'inline';
  className?: string;
}

const ProfileCompletionWidget: React.FC<ProfileCompletionWidgetProps> = ({
  profile,
  onNavigate,
  variant = 'floating',
  className = '',
}) => {
  const { t } = useTranslation();
  const pct = useMemo(() => computeProfileCompletenessPct(profile), [profile]);
  const displayPct = useAnimatedPct(pct);
  const [hiddenForever, setHiddenForever] = useState(() => readHiddenPreference());
  const [expanded, setExpanded] = useState(() =>
    variant === 'inline' ? true : !readMinimizedSnapshot(),
  );

  const handleDismissForever = () => {
    writeHiddenPreference(true);
    setHiddenForever(true);
    setExpanded(false);
  };

  const handleMinimize = () => {
    setExpanded(false);
    writeMinimizedSnapshot(true);
  };

  const handleExpand = () => {
    setExpanded(true);
    writeMinimizedSnapshot(false);
  };

  const handleCompleteProfile = () => {
    const firstPending = buildCheckItems(profile).find((i) => !i.done);
    onNavigate(firstPending?.subTab ?? 'work', firstPending?.key);
    if (variant === 'floating') handleMinimize();
  };

  if (hiddenForever && variant === 'floating') {
    return null;
  }

  if (pct >= 100 && variant === 'floating') {
    return null;
  }

  if (variant === 'inline') {
    return (
      <ExpandedCard
        profile={profile}
        pct={pct}
        displayPct={displayPct}
        onNavigate={onNavigate}
        onCompleteProfile={handleCompleteProfile}
        className={className}
      />
    );
  }

  return (
    <div
      className={`profile-completion-widget relative flex flex-col items-end gap-3 ${expanded ? 'profile-completion-widget--expanded' : ''} ${className}`}
    >
      {expanded && (
        <ExpandedCard
          profile={profile}
          pct={pct}
          displayPct={displayPct}
          onMinimize={handleMinimize}
          showDismissOption
          onDismissForever={handleDismissForever}
          onNavigate={(subTab, sectionKey) => {
            onNavigate(subTab, sectionKey);
            handleMinimize();
          }}
          onCompleteProfile={handleCompleteProfile}
        />
      )}

      {!expanded && (
        <button
          type="button"
          onClick={handleExpand}
          aria-label={t('dashboard.profileCompletion.ariaExpand', { defaultValue: 'Profile {{pct}}% complete — expand checklist', pct })}
          aria-expanded={false}
          className="profile-completion-fab flex h-[72px] w-[72px] items-center justify-center rounded-2xl transition-all active:scale-[0.97] touch-manipulation"
        >
          <ProgressRing pct={displayPct} size="sm" />
        </button>
      )}
    </div>
  );
};

export default ProfileCompletionWidget;
